import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import { flow, random } from 'lodash/fp'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { BrandFeatureWithUsernameAndImage } from 'shared/src/api/types'
import { trackFeaturedBrandClick } from 'shared/src/utils/analytics'
import cx from 'classnames'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { serifFontFamily } from 'utils/styles'
import { LinkOrButton } from 'components/LinkOrButton'
import { makeBrandUrl, makeFluidCreatorUrl } from 'utils/routes'

const getRandomBrand: (
  features: BrandFeatureWithUsernameAndImage[]
) => BrandFeatureWithUsernameAndImage = flow(
  (features) => features[random(0, features.length - 1)]
)

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
  },
  titleText: {
    color: '#FFF',
    fontSize: 32,
    fontWeight: 700,
    fontFamily: serifFontFamily,
    lineHeight: '129.5%',
    letterSpacing: 0,
    marginLeft: 32,
    marginBottom: 38,
  },
  imageContainer: {
    alignSelf: 'center',
    width: '95%',
    height: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
})

interface Props {
  brandFeatures: BrandFeatureWithUsernameAndImage[]
  className?: string
}

export const BrandFeature: FC<Props> = flowMax(
  addDisplayName('BrandFeatures'),
  addProps(
    ({ brandFeatures }) => ({
      feature: getRandomBrand(brandFeatures),
    }),
    ['brandFeatures']
  ),
  addProps(
    ({ feature }) => ({
      navigateTo: feature.username
        ? makeFluidCreatorUrl(feature.username)
        : makeBrandUrl(feature.brandId),
    }),
    ['feature']
  ),
  addHandlers({
    onClick:
      ({ feature }) =>
      () => {
        trackFeaturedBrandClick(feature.brandId)
      },
  }),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    feature: { image, username },
    navigateTo,
    onClick,
    className,
    classes,
    t,
  }) => (
    <div className={cx(classes.container, className)}>
      <div className={classes.titleText}>{t('feed.featuredBrandTitle')}</div>
      <LinkOrButton
        navigateTo={navigateTo}
        onClick={onClick}
        showBackButton
        className={classes.imageContainer}
      >
        <img
          src={image.full}
          className={classes.image}
          alt={t('feed.featuredBrandImageAlt', { brand: username })}
        />
      </LinkOrButton>
    </div>
  )
)
